import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Sparkles, Wand2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { generateJobDraft, type AiJobDraft } from "@/lib/ai-job-builder.functions";
import { PriceEstimateCard } from "@/components/site/PriceEstimateCard";

type Category = { id: string; slug: string; name: string };

type Applied = {
  category_id?: string;
  title: string;
  description: string;
  estimated_duration_minutes: number;
  urgency: "low" | "normal" | "high";
};

export function AiJobBuilder({
  categories,
  postcode,
  onApply,
}: {
  categories: Category[];
  postcode?: string;
  onApply: (a: Applied, meta: AiJobDraft) => void;
}) {
  const generate = useServerFn(generateJobDraft);
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState<AiJobDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const examples = [
    "Mount a 55″ TV above my fireplace in Clapham",
    "Assemble an IKEA Pax wardrobe (3 doors) in my bedroom",
    "Need a man and van to move a sofa from Hackney to Islington",
    "Hang 4 heavy mirrors on a plasterboard wall",
  ];

  async function onGenerate(text?: string) {
    const p = (text ?? prompt).trim();
    if (p.length < 5) {
      setErr("Describe what you need in a sentence or two.");
      return;
    }
    setErr(null);
    setLoading(true);
    try {
      const result = await generate({ data: { prompt: p } });
      setDraft(result);
      setPrompt(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't generate — please try again.");
    } finally {
      setLoading(false);
    }
  }

  function applyDraft() {
    if (!draft) return;
    const cat = categories.find((c) => c.slug === draft.category_slug);
    onApply(
      {
        category_id: cat?.id,
        title: draft.title,
        description: draft.description,
        estimated_duration_minutes: draft.estimated_duration_minutes,
        urgency: draft.urgency,
      },
      draft,
    );
    toast.success("AI draft applied — review and edit anything before posting.");
  }

  return (
    <section
      aria-labelledby="ai-builder-heading"
      className="mb-6 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card shadow-card"
    >
      <div className="border-b border-border bg-primary/5 px-5 py-3 sm:px-6">
        <h2 id="ai-builder-heading" className="flex items-center gap-2 text-sm font-semibold">
          <Wand2 className="h-4 w-4 text-primary" aria-hidden />
          Describe it in your own words — we'll fill the form
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Type one sentence. We'll suggest the service, summary, skills and duration. You always review before posting.
        </p>
      </div>

      <div className="space-y-3 p-5 sm:p-6">
        <label htmlFor="ai-prompt" className="sr-only">
          Describe your job
        </label>
        <Textarea
          id="ai-prompt"
          rows={2}
          maxLength={1000}
          placeholder="e.g. I need a TV mounted above my fireplace in Clapham, brick wall, 55 inch."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="text-base"
        />

        <div className="flex flex-wrap gap-1.5">
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => onGenerate(ex)}
              className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
              disabled={loading}
            >
              {ex}
            </button>
          ))}
        </div>

        {err && (
          <p role="alert" className="text-xs text-destructive">
            {err}
          </p>
        )}

        <Button
          type="button"
          onClick={() => onGenerate()}
          disabled={loading || prompt.trim().length < 5}
          className="w-full sm:w-auto"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Drafting your job…
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" /> Draft my job with AI
            </>
          )}
        </Button>

        {draft && !loading && (
          <div className="mt-2 space-y-3 rounded-xl border border-border bg-background p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                <Check className="h-3 w-3" /> AI draft ready · review before posting
              </span>
              <span className="text-[11px] text-muted-foreground">
                Confidence {Math.round(draft.confidence * 100)}%
              </span>
            </div>

            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Service</dt>
                <dd className="font-medium capitalize">{draft.category_slug.replace(/-/g, " ")}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Estimated time</dt>
                <dd className="font-medium">
                  {draft.estimated_duration_minutes >= 60
                    ? `${Math.round((draft.estimated_duration_minutes / 60) * 10) / 10} hr`
                    : `${draft.estimated_duration_minutes} min`}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">Title</dt>
                <dd className="font-medium">{draft.title}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">Summary</dt>
                <dd className="text-foreground">{draft.description}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="mb-1 text-xs font-medium text-muted-foreground">Required skills</dt>
                <dd className="flex flex-wrap gap-1">
                  {draft.required_skills.map((s) => (
                    <span
                      key={s}
                      className="rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[11px]"
                    >
                      {s}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>

            <p className="text-[11px] text-muted-foreground">{draft.rationale}</p>

            {postcode && (
              <PriceEstimateCard
                categorySlug={draft.category_slug}
                postcode={postcode}
                estimatedDurationMinutes={draft.estimated_duration_minutes}
                urgency={draft.urgency}
              />
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" onClick={applyDraft} className="sm:flex-1">
                <Check className="mr-2 h-4 w-4" /> Use this draft
              </Button>
              <Button type="button" variant="outline" onClick={() => onGenerate()} disabled={loading}>
                Regenerate
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
